import { saveAs } from 'file-saver';
import { ToastrService } from 'ngx-toastr';
import { Observable, throwError, of, Subject } from 'rxjs';
import { delay, map, switchMap, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';

import { ISnListResponse, ISnListQueryParameters } from '@lib/api';
import { SnBulkClip, SnRecording, SnRecordingUrls, SnShare } from '@lib/api/entities';
import { ISnCanShare } from '@lib/share';
import { t } from '@lib/translation';
import { SnDialogService } from '@lib/ui/dialog';

@Injectable({ providedIn: 'root' })
export class SnRecordingsAPI implements OnDestroy, ISnCanShare {
  public clipLoading: boolean;
  public isDownloading = false;
  public percentDone = 0;
  public secondsLeft = 0;
  public fileSize = 0;
  public downloadedSize = 0;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public getShareList(
    recordingPk: string,
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnShare>> {
    return this._httpClient.get(`/api/recordings/${recordingPk}/shares/`, { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((share) => new SnShare(share)),
      })),
    );
  }

  public createShare(recordingPk: string, data: any): Observable<SnShare | SnShare[]> {
    return this._httpClient.post(`/api/recordings/${recordingPk}/shares/`, data).pipe(
      map((response: any) => {
        return Array.isArray(response)
          ? response.map((share) => new SnShare(share))
          : new SnShare(response);
      }),
    );
  }

  public updateShare(recordingPk: string, shareId: string, data: any): Observable<SnShare> {
    return this._httpClient
      .patch(`/api/recordings/${recordingPk}/shares/${shareId}/`, data)
      .pipe(map((response: any) => new SnShare(response)));
  }

  public deleteShare(recordingPk: string, shareId: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('shares.confirmDeleteShare'),
        confirmButton: {
          text: t('common.delete').toUpperCase(),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => {
          return this._httpClient.delete(`/api/recordings/${recordingPk}/shares/${shareId}/`);
        }),
      );
  }

  public sendClip(data: SnBulkClip): Observable<any> {
    return this._httpClient.post('/api/recordings/bulk/', data).pipe(
      map((response: any) => {
        return response.results;
      }),
      tap({
        next: () => this._toastrService.success(t('recordings.clipsAvailableSoon')),
        error: () => this._toastrService.error(t('recordings.unableToCreateClips')),
      }),
    );
  }

  public getIncidentList(
    params?: ISnRecordingListQueryParameters,
  ): Observable<ISnListResponse<SnRecording>> {
    return this._httpClient.get('/api/recordings/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((incident) => new SnRecording(incident)),
      })),
    );
  }

  public getUserIncidentList(
    userId: string,
    params?: ISnRecordingListQueryParameters,
  ): Observable<ISnListResponse<SnRecording>> {
    return this._httpClient.get(`/api/users/${userId}/incidents/`, { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((incident) => new SnRecording(incident)),
      })),
    );
  }

  public getRawIncident(id: string): Observable<Object> {
    return this._httpClient.get(`/api/recordings/${id}/`);
  }

  public getIncident(id: string): Observable<SnRecording> {
    return this.getRawIncident(id).pipe(map((response: any) => new SnRecording(response)));
  }

  public retryClip(id: string, data: any): Observable<SnRecording> {
    return this._httpClient.post<SnRecording>(`/api/recordings/${id}/videoclip-retry/`, data);
  }

  public getIncidentUrls(incident: SnRecording): Observable<SnRecordingUrls> {
    if (!incident) {
      return throwError('Invalid Incident object');
    }

    if (incident.fileIsAvailable && incident.durationSeconds) {
      return this._httpClient.get(`/api/recordings/${incident.id}/urls/`).pipe(
        map((response: any) => new SnRecordingUrls(response)),
        tap((urls) => {
          incident.urls = urls;
        }),
      );
    }

    const incidentUrls = new SnRecordingUrls({
      incident: { url: '/assets/video-status/fusus-video-placeholder.mp4' },
      thumbnail: { url: '/assets/video-status/fusus-video-placeholder.png' },
    });

    incident.urls = incidentUrls;

    return of(incidentUrls).pipe(delay(300));
  }

  public downloadIncidentFile(incident: SnRecording): Observable<string> {
    if (!incident.fileIsAvailable) {
      this._toastrService.error(t('recordings.videoFileNotFound'));
      return throwError('Video not found');
    }
    return this.getIncidentUrls(incident).pipe(
      map((urls) => urls.incident),
      tap((incidentUrl: any) => {
        window.open(incidentUrl, '_blank');
      }),
    );
  }

  public downloadIncidentMeta(incident: SnRecording): void {
    const incidentJSON = JSON.stringify(incident.toMetaJSON());
    const incidentBlob = new Blob([incidentJSON], { type: 'octet/stream' });

    saveAs(incidentBlob, `${incident.id}.json`);
  }

  public updateIncident(id: string, data: any): Observable<SnRecording> {
    return this._httpClient.patch(`/api/recordings/${id}/`, data).pipe(
      map((response: any) => new SnRecording(response)),
      tap({
        next: () => this._toastrService.success(t('recordings.recordingUpdated')),
        error: () =>
          this._toastrService.error(t('recordings.unableToUpdateRecording')),
      }),
    );
  }

  public deleteIncident(id: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('recordings.confirmDeleteEvidence'),
        confirmButton: {
          text: t('common.delete').toUpperCase(),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/recordings/${id}/`)),
        tap({
          next: () => this._toastrService.success(t('recordings.evidenceDeleted')),
          error: () =>
            this._toastrService.error(
              t('recordings.unableToDeleteEvidence'),
            ),
        }),
      );
  }
}

export interface ISnRecordingListQueryParameters extends ISnListQueryParameters {
  endTimestamp__lt?: Date;
  endTimestamp__lte?: Date;
  endTimestamp__gt?: Date;
  endTimestamp__gte?: Date;
  startTimestamp__te?: Date;
  startTimestamp__lte?: Date;
  startTimestamp__gt?: Date;
  startTimestamp__gte?: Date;
  team?: string;
  expireSoon?: boolean;
}
